// src/pages/teacher/TeacherPurchaseLists.tsx
// רשימות קניות — רשימה, פירוט, הוספת מוצרים, סימון "נרכש", המרה להוצאה

import { useState, useEffect } from 'react';
import { ShoppingCart, Plus, X, Check, Trash2, ArrowRight, Package, Search, ChevronDown, ChevronUp, Send, Receipt, PackageCheck } from 'lucide-react';
import { teacherApi } from '../../services/api';
import { useTeacherStore, purchaseListStatusNames, type PurchaseList, type PurchaseListItem } from '../../stores/teacherStore';
import { useAuthStore } from '../../stores/authStore';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { teacherPurchaseHelpTexts } from '../../constants/helpTexts';
import DeleteConfirmModal from '../../components/shared/DeleteConfirmModal';
import { teacherColors as colors } from '../../styles/colors';

const statusColors: Record<number, { bg: string; text: string }> = {
  0: { bg: '#f3f4f6', text: '#6b7280' },   // Draft
  1: { bg: '#fef3c7', text: '#92400e' },   // Sent
  2: { bg: '#dcfce7', text: '#166534' },   // Purchased
  3: { bg: '#ede9fe', text: '#6d28d9' },   // Invoiced
};

interface PurchaseListDetail extends PurchaseList {
  items: PurchaseListItem[];
}

export default function TeacherPurchaseLists() {
  const { purchaseLists, setPurchaseLists, budgetSources, setBudgetSources, catalog, setCatalog, categories, setCategories, isLoadingPurchases, setLoading } = useTeacherStore();
  const { user } = useAuthStore();

  // Views: 'list' | 'detail'
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedList, setSelectedList] = useState<PurchaseListDetail | null>(null);

  // Filter
  const [filterStatus, setFilterStatus] = useState<string>('');

  // Create list modal
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSourceId, setNewSourceId] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [creating, setCreating] = useState(false);

  // Add item modal
  const [showAddItem, setShowAddItem] = useState(false);
  const [addMode, setAddMode] = useState<'catalog' | 'adhoc'>('catalog');
  const [searchText, setSearchText] = useState('');
  const [adHocName, setAdHocName] = useState('');
  const [adHocQty, setAdHocQty] = useState('1');
  const [adHocUnit, setAdHocUnit] = useState('');
  const [adHocPrice, setAdHocPrice] = useState('');
  const [adHocChargeable, setAdHocChargeable] = useState(true);
  const [addingItem, setAddingItem] = useState(false);

  // Catalog quantity selector
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [catalogQty, setCatalogQty] = useState('1');

  // Received tracking
  const [receivingItemId, setReceivingItemId] = useState<number | null>(null);
  const [receivedQty, setReceivedQty] = useState('');

  // Convert modal
  const [showConvert, setShowConvert] = useState(false);
  const [convertSourceId, setConvertSourceId] = useState('');
  const [converting, setConverting] = useState(false);

  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ publicId: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading('purchases', true);
    try {
      const [listRes, budgetRes, catRes] = await Promise.all([
        teacherApi.getPurchaseLists(),
        teacherApi.getBudgetSources(),
        teacherApi.getCategories(),
      ]);
      if (listRes.success && listRes.data) setPurchaseLists(listRes.data);
      if (budgetRes.success && budgetRes.data) setBudgetSources(budgetRes.data);
      if (catRes.success && catRes.data) setCategories(catRes.data);
    } catch (err) { console.error('Failed to load:', err); }
    finally { setLoading('purchases', false); }
  };

  const loadCatalog = async () => {
    try {
      const res = await teacherApi.getProducts();
      if (res.success && res.data) setCatalog(res.data);
    } catch (err) { console.error(err); }
  };

  const openDetail = async (list: PurchaseList) => {
    try {
      const res = await teacherApi.getPurchaseList(list.publicId);
      if (res.success && res.data) {
        setSelectedList(res.data as PurchaseListDetail);
        setView('detail');
      }
    } catch (err) { console.error(err); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { setError('יש להזין שם לרשימה'); return; }
    setCreating(true); setError('');
    try {
      const res = await teacherApi.createPurchaseList({
        kindergartenPublicId: user?.kindergartenPublicId,
        name: newName.trim(),
        budgetSourcePublicId: newSourceId || undefined,
        notes: newNotes.trim() || undefined,
      });
      if (res.success) { setShowCreate(false); resetCreateForm(); await loadData(); }
      else setError(res.message || 'שגיאה');
    } catch (err: any) { setError(err.response?.data?.message || 'שגיאה'); }
    finally { setCreating(false); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await teacherApi.deletePurchaseList(deleteTarget.publicId);
      if (res.success) {
        setDeleteTarget(null);
        if (view === 'detail') { setView('list'); setSelectedList(null); }
        await loadData();
      }
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  const handleAddCatalogItem = async (product: any, quantity: number) => {
    if (!selectedList) return;
    setAddingItem(true);
    try {
      const res = await teacherApi.addPurchaseItem(selectedList.publicId, {
        productId: product.id,
        productName: product.name,
        categoryId: product.categoryId,
        quantity,
        unit: product.defaultUnit,
        estimatedPrice: product.estimatedPrice,
        isChargeable: product.isChargeable,
      });
      if (res.success) { setExpandedProductId(null); setCatalogQty('1'); await refreshDetail(); }
    } catch (err) { console.error(err); }
    finally { setAddingItem(false); }
  };

  const handleAddAdHocItem = async () => {
    if (!selectedList || !adHocName.trim()) return;
    setAddingItem(true);
    try {
      const res = await teacherApi.addPurchaseItem(selectedList.publicId, {
        productName: adHocName.trim(),
        quantity: parseFloat(adHocQty) || 1,
        unit: adHocUnit.trim() || undefined,
        estimatedPrice: adHocPrice ? parseFloat(adHocPrice) : undefined,
        isChargeable: adHocChargeable,
      });
      if (res.success) {
        resetAdHocForm();
        await refreshDetail();
      }
    } catch (err) { console.error(err); }
    finally { setAddingItem(false); }
  };

  const handleToggleItem = async (itemId: number) => {
    if (!selectedList) return;
    try {
      const res = await teacherApi.togglePurchaseItem(selectedList.publicId, itemId);
      if (res.success) await refreshDetail();
    } catch (err) { console.error(err); }
  };

  const handleRemoveItem = async (itemId: number) => {
    if (!selectedList) return;
    try {
      const res = await teacherApi.removePurchaseItem(selectedList.publicId, itemId);
      if (res.success) await refreshDetail();
    } catch (err) { console.error(err); }
  };

  const handleMarkReceived = async (itemId: number) => {
    if (!selectedList) return;
    try {
      const qty = parseFloat(receivedQty) || undefined;
      const res = await teacherApi.updatePurchaseItem(selectedList.publicId, itemId, { isReceived: true, receivedQuantity: qty });
      if (res.success) { setReceivingItemId(null); setReceivedQty(''); await refreshDetail(); }
    } catch (err) { console.error(err); }
  };

  const handleConvert = async () => {
    if (!selectedList || !convertSourceId) return;
    setConverting(true); setError('');
    try {
      const res = await teacherApi.convertToExpense(selectedList.publicId, convertSourceId);
      if (res.success) {
        setShowConvert(false);
        setConvertSourceId('');
        await refreshDetail();
        await loadData();
      } else setError(res.message || 'שגיאה בהמרה');
    } catch (err: any) { setError(err.response?.data?.message || 'שגיאה בהמרה'); }
    finally { setConverting(false); }
  };

  const refreshDetail = async () => {
    if (!selectedList) return;
    try {
      const res = await teacherApi.getPurchaseList(selectedList.publicId);
      if (res.success && res.data) setSelectedList(res.data as PurchaseListDetail);
    } catch (err) { console.error(err); }
  };

  const resetCreateForm = () => { setNewName(''); setNewSourceId(''); setNewNotes(''); setError(''); };
  const resetAdHocForm = () => { setAdHocName(''); setAdHocQty('1'); setAdHocUnit(''); setAdHocPrice(''); setAdHocChargeable(true); };

  const filtered = purchaseLists.filter(pl => {
    if (filterStatus && pl.status.toString() !== filterStatus) return false;
    return true;
  });

  const fmt = (n: number) => `${n.toLocaleString('he-IL')}₪`;
  const fmtDate = (s: string) => { try { return new Date(s).toLocaleDateString('he-IL'); } catch { return s; } };

  const filteredCatalog = catalog.filter(p =>
    p.name.includes(searchText) || (p.categoryName && p.categoryName.includes(searchText))
  );

  // ==============================
  // DETAIL VIEW
  // ==============================
  if (view === 'detail' && selectedList) {
    const sc = statusColors[selectedList.status] || statusColors[0];
    const items = selectedList.items || [];
    const purchasedCount = items.filter(i => i.isPurchased).length;
    const receivedCount = items.filter(i => i.isReceived).length;
    const totalEst = items.reduce((s, i) => s + (i.estimatedPrice || 0) * i.quantity, 0);
    const hasPurchased = items.some(i => i.isPurchased);
    const canConvert = selectedList.status < 3 && hasPurchased;

    return (
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        {/* Back button */}
        <button onClick={() => { setView('list'); setSelectedList(null); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 0', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: colors.primary, fontSize: '14px', fontWeight: '500', marginBottom: '12px' }}>
          <ArrowRight size={18} /> חזרה לרשימות
        </button>

        {/* List header */}
        <div style={{ backgroundColor: colors.white, borderRadius: '14px', padding: '16px', border: `1px solid ${colors.border}`, marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, margin: 0 }}>{selectedList.name}</h2>
                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', backgroundColor: sc.bg, color: sc.text, fontWeight: '500' }}>
                  {selectedList.statusName || purchaseListStatusNames[selectedList.status]}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: colors.textLight }}>
                <span>{fmtDate(selectedList.createdAt)}</span>
                {selectedList.budgetSourceName && <span>{selectedList.budgetSourceName}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {canConvert && (
                <button onClick={() => { setShowConvert(true); setError(''); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px', backgroundColor: colors.greenLight, color: '#166534', border: `1px solid ${colors.green}40`, borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}>
                  <Receipt size={14} /> המר להוצאה
                </button>
              )}
              <button onClick={() => setDeleteTarget({ publicId: selectedList.publicId, name: selectedList.name })} style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>
                <Trash2 size={16} color={colors.textLight} />
              </button>
            </div>
          </div>

          {/* Progress */}
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: colors.textLight }}>
            <span>{purchasedCount}/{items.length} נרכשו{receivedCount > 0 ? ` · ${receivedCount} נתקבלו` : ''}</span>
            {totalEst > 0 && <span>הערכה: {fmt(totalEst)}</span>}
          </div>
          {items.length > 0 && (
            <div style={{ marginTop: '6px', height: '4px', backgroundColor: colors.border, borderRadius: '2px', overflow: 'hidden', position: 'relative' }}>
              <div style={{ height: '100%', width: `${(purchasedCount / items.length) * 100}%`, backgroundColor: '#86efac', borderRadius: '2px', transition: 'width 0.3s', position: 'absolute', top: 0, right: 0 }} />
              <div style={{ height: '100%', width: `${(receivedCount / items.length) * 100}%`, backgroundColor: colors.green, borderRadius: '2px', transition: 'width 0.3s', position: 'absolute', top: 0, right: 0 }} />
            </div>
          )}
        </div>

        {/* Add item button */}
        {selectedList.status < 3 && (
          <button onClick={() => { setShowAddItem(true); setAddMode('catalog'); setSearchText(''); loadCatalog(); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '12px', backgroundColor: colors.primaryLight, color: colors.primary, border: `1px dashed ${colors.primary}60`, borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', marginBottom: '12px' }}>
            <Plus size={18} /> הוסיפי מוצר
          </button>
        )}

        {/* Items list */}
        {items.length === 0 && (
          <div style={{ backgroundColor: colors.white, borderRadius: '14px', padding: '32px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
            <Package size={32} color={colors.textLight} style={{ margin: '0 auto 8px' }} />
            <p style={{ fontSize: '14px', color: colors.textLight }}>אין מוצרים ברשימה</p>
          </div>
        )}

        {items.map(item => (
          <div key={item.id} style={{ backgroundColor: colors.white, borderRadius: '10px', border: `1px solid ${item.isReceived ? colors.green + '40' : colors.border}`, padding: '12px 14px', marginBottom: '8px', opacity: item.isPurchased && !item.isReceived ? 0.7 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Checkbox */}
              <button onClick={() => handleToggleItem(item.id)} style={{ width: '24px', height: '24px', borderRadius: '6px', border: `2px solid ${item.isPurchased ? colors.green : colors.border}`, backgroundColor: item.isPurchased ? colors.greenLight : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                {item.isPurchased && <Check size={14} color={colors.green} />}
              </button>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: colors.text, textDecoration: item.isPurchased ? 'line-through' : 'none' }}>{item.productName}</span>
                  {item.isAdHoc && <span style={{ fontSize: '9px', padding: '1px 4px', borderRadius: '3px', backgroundColor: colors.yellowLight, color: '#92400e' }}>מזדמן</span>}
                  {item.isReceived && <span style={{ fontSize: '9px', padding: '1px 4px', borderRadius: '3px', backgroundColor: colors.greenLight, color: '#166534' }}>✓ נתקבל</span>}
                </div>
                <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: colors.textLight, marginTop: '2px', flexWrap: 'wrap' }}>
                  <span>{item.quantity} {item.unit || 'יח׳'}</span>
                  {item.isReceived && item.receivedQuantity != null && item.receivedQuantity !== item.quantity && (
                    <span style={{ color: '#b45309' }}>נתקבל: {item.receivedQuantity} {item.unit || 'יח׳'}</span>
                  )}
                  {item.estimatedPrice != null && <span>{fmt(item.estimatedPrice)}</span>}
                  {item.categoryName && <span>{item.categoryName}</span>}
                  {!item.isChargeable && <span style={{ color: colors.green }}>חינם</span>}
                </div>
                {item.isReceived && item.receivedAt && (
                  <div style={{ fontSize: '10px', color: '#166534', marginTop: '2px' }}>
                    נתקבל {new Date(item.receivedAt).toLocaleDateString('he-IL')}{item.receivedByName ? ` ע״י ${item.receivedByName}` : ''}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {/* Mark received button — only for purchased, not yet received items */}
                {item.isPurchased && !item.isReceived && (
                  <button onClick={() => { setReceivingItemId(receivingItemId === item.id ? null : item.id); setReceivedQty(item.quantity.toString()); }} style={{ padding: '4px 8px', backgroundColor: colors.greenLight, border: `1px solid ${colors.green}40`, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#166534', fontWeight: '500', whiteSpace: 'nowrap' }}>
                    <PackageCheck size={12} style={{ verticalAlign: 'middle', marginLeft: '2px' }} /> נתקבל
                  </button>
                )}
                {selectedList.status < 3 && (
                  <button onClick={() => handleRemoveItem(item.id)} style={{ padding: '4px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>
                    <X size={14} color={colors.textLight} />
                  </button>
                )}
              </div>
            </div>

            {/* Inline received quantity form */}
            {receivingItemId === item.id && (
              <div style={{ marginTop: '8px', padding: '8px 10px', backgroundColor: '#f0fdf4', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FormLabel text="כמות שנתקבלה" helpText={teacherPurchaseHelpTexts.receivedQuantity.tooltip} style={{ fontSize: '12px', color: '#166534', whiteSpace: 'nowrap', marginBottom: 0 }} />
                <input type="number" value={receivedQty} onChange={e => setReceivedQty(e.target.value)} style={{ width: '80px', padding: '6px 8px', fontSize: '13px', border: '1px solid #bbf7d0', borderRadius: '6px', outline: 'none', textAlign: 'center', direction: 'ltr' }} />
                <span style={{ fontSize: '12px', color: colors.textLight }}>{item.unit || 'יח׳'}</span>
                <button onClick={() => handleMarkReceived(item.id)} style={{ padding: '6px 12px', backgroundColor: colors.green, color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  אשר קבלה
                </button>
                <button onClick={() => { setReceivingItemId(null); setReceivedQty(''); }} style={{ padding: '6px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>
                  <X size={14} color={colors.textLight} />
                </button>
              </div>
            )}
          </div>
        ))}

        {selectedList.notes && (
          <div style={{ marginTop: '8px', fontSize: '12px', color: colors.textLight, padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            הערות: {selectedList.notes}
          </div>
        )}

        {/* Add item modal */}
        {showAddItem && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowAddItem(false); resetAdHocForm(); setExpandedProductId(null); setCatalogQty('1'); } }}>
            <div style={{ backgroundColor: colors.white, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '600px', maxHeight: '85vh', overflow: 'auto', padding: '24px' }} onMouseDown={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, margin: 0 }}>הוספת מוצר</h2>
                <button onClick={() => { setShowAddItem(false); resetAdHocForm(); setExpandedProductId(null); setCatalogQty('1'); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}>
                  <X size={22} color={colors.textLight} />
                </button>
              </div>

              {/* Tab: catalog / ad-hoc */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={() => setAddMode('catalog')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${addMode === 'catalog' ? colors.primary : colors.border}`, backgroundColor: addMode === 'catalog' ? colors.primaryLight : colors.white, color: addMode === 'catalog' ? colors.primary : colors.textLight, fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                  מקטלוג
                </button>
                <button onClick={() => setAddMode('adhoc')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${addMode === 'adhoc' ? colors.primary : colors.border}`, backgroundColor: addMode === 'adhoc' ? colors.primaryLight : colors.white, color: addMode === 'adhoc' ? colors.primary : colors.textLight, fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                  מוצר מזדמן
                </button>
              </div>

              {addMode === 'catalog' && (
                <>
                  {/* Search */}
                  <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <Search size={16} color={colors.textLight} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="חיפוש מוצר..." style={{ ...inputStyle, paddingRight: '36px' }} />
                  </div>

                  {/* Catalog list */}
                  <div style={{ maxHeight: '350px', overflow: 'auto' }}>
                    {filteredCatalog.length === 0 && (
                      <p style={{ textAlign: 'center', color: colors.textLight, fontSize: '13px', padding: '16px' }}>
                        {catalog.length === 0 ? 'טוען קטלוג...' : 'לא נמצאו מוצרים'}
                      </p>
                    )}
                    {filteredCatalog.map(product => {
                      const isExpanded = expandedProductId === product.publicId;
                      return (
                        <div key={product.publicId} style={{ marginBottom: '6px', borderRadius: '8px', border: `1px solid ${isExpanded ? colors.primary + '60' : colors.border}`, overflow: 'hidden' }}>
                          <button onClick={() => { setExpandedProductId(isExpanded ? null : product.publicId); setCatalogQty('1'); }} disabled={addingItem} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', backgroundColor: isExpanded ? colors.primaryLight : colors.white, border: 'none', cursor: addingItem ? 'not-allowed' : 'pointer', textAlign: 'right' }}>
                            <div>
                              <span style={{ fontSize: '14px', fontWeight: '500', color: colors.text }}>{product.name}</span>
                              <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: colors.textLight, marginTop: '2px' }}>
                                {product.categoryName && <span>{product.categoryName}</span>}
                                {product.defaultUnit && <span>{product.defaultUnit}</span>}
                                {product.estimatedPrice != null && <span>{fmt(product.estimatedPrice)}</span>}
                              </div>
                            </div>
                            {isExpanded ? <ChevronUp size={16} color={colors.primary} /> : <Plus size={16} color={colors.primary} />}
                          </button>
                          {isExpanded && (
                            <div style={{ padding: '8px 12px', backgroundColor: colors.primaryLight, display: 'flex', alignItems: 'center', gap: '8px', borderTop: `1px solid ${colors.primary}20` }}>
                              <label style={{ fontSize: '12px', color: colors.primary, whiteSpace: 'nowrap', fontWeight: '500' }}>כמות:</label>
                              <input type="number" value={catalogQty} onChange={e => setCatalogQty(e.target.value)} min="0.1" step="0.5" style={{ width: '70px', padding: '6px 8px', fontSize: '13px', border: `1px solid ${colors.primary}40`, borderRadius: '6px', outline: 'none', textAlign: 'center', direction: 'ltr', backgroundColor: colors.white }} />
                              <span style={{ fontSize: '12px', color: colors.textLight }}>{product.defaultUnit || 'יח׳'}</span>
                              <button onClick={() => handleAddCatalogItem(product, parseFloat(catalogQty) || 1)} disabled={addingItem} style={{ marginRight: 'auto', padding: '6px 14px', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: addingItem ? 'not-allowed' : 'pointer' }}>
                                {addingItem ? 'מוסיף...' : 'הוסף'}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {addMode === 'adhoc' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <FormLabel text="שם המוצר" required helpText={teacherPurchaseHelpTexts.adHocName.tooltip} />
                    <input value={adHocName} onChange={e => setAdHocName(e.target.value)} placeholder="שם המוצר" style={inputStyle} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <FormLabel text="כמות" helpText={teacherPurchaseHelpTexts.adHocQuantity.tooltip} />
                      <input type="number" value={adHocQty} onChange={e => setAdHocQty(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <FormLabel text="יחידה" helpText={teacherPurchaseHelpTexts.adHocUnit.tooltip} />
                      <input value={adHocUnit} onChange={e => setAdHocUnit(e.target.value)} placeholder="יח׳ / ק״ג" style={inputStyle} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <FormLabel text="מחיר משוער" helpText={teacherPurchaseHelpTexts.adHocPrice.tooltip} />
                      <input type="number" value={adHocPrice} onChange={e => setAdHocPrice(e.target.value)} placeholder="0.00" style={inputStyle} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: colors.text, cursor: 'pointer' }}>
                        <input type="checkbox" checked={adHocChargeable} onChange={e => setAdHocChargeable(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: colors.primary }} />
                        מחויב בתשלום
                      </label>
                    </div>
                  </div>
                  <button onClick={handleAddAdHocItem} disabled={addingItem || !adHocName.trim()} style={{ width: '100%', padding: '12px', backgroundColor: addingItem || !adHocName.trim() ? colors.border : colors.primary, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: addingItem || !adHocName.trim() ? 'not-allowed' : 'pointer' }}>
                    {addingItem ? 'מוסיף...' : 'הוסף מוצר'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Convert to expense modal */}
        {showConvert && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowConvert(false); }}>
            <div style={{ backgroundColor: colors.white, borderRadius: '16px', width: '100%', maxWidth: '400px', padding: '24px' }} onMouseDown={e => e.stopPropagation()}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '4px' }}>המרה להוצאה</h3>
              <p style={{ fontSize: '13px', color: colors.textLight, marginBottom: '16px' }}>
                הפריטים שסומנו כנרכשו יומרו להוצאה תקציבית
              </p>
              {error && <div style={{ backgroundColor: colors.redLight, color: '#991b1b', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}
              <FormLabel text="מקור תקציב" required helpText={teacherPurchaseHelpTexts.convertSource.tooltip} />
              <select value={convertSourceId} onChange={e => setConvertSourceId(e.target.value)} style={inputStyle}>
                <option value="">בחרי מקור...</option>
                {budgetSources.filter(bs => bs.isActive).map(bs => (
                  <option key={bs.publicId} value={bs.publicId}>{bs.name} (נותר: {fmt(bs.remainingBudget)})</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button onClick={() => { setShowConvert(false); setError(''); }} style={{ flex: 1, padding: '12px', backgroundColor: colors.white, color: colors.textLight, border: `1px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px', cursor: 'pointer' }}>ביטול</button>
                <button onClick={handleConvert} disabled={converting || !convertSourceId} style={{ flex: 1, padding: '12px', backgroundColor: converting || !convertSourceId ? colors.border : colors.green, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: converting || !convertSourceId ? 'not-allowed' : 'pointer' }}>
                  {converting ? 'ממיר...' : 'המר להוצאה'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==============================
  // LIST VIEW
  // ==============================
  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: 0 }}>רשימות קניות</h1>
        <button onClick={() => { setShowCreate(true); resetCreateForm(); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
          <Plus size={18} /> רשימה חדשה
        </button>
      </div>

      {/* Status filter pills */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', overflowX: 'auto', paddingBottom: '4px' }}>
        <button onClick={() => setFilterStatus('')} style={{ ...pillStyle, backgroundColor: !filterStatus ? colors.primaryLight : colors.white, color: !filterStatus ? colors.primary : colors.textLight, border: `1px solid ${!filterStatus ? colors.primary + '40' : colors.border}` }}>
          הכל ({purchaseLists.length})
        </button>
        {[0, 1, 2, 3].map(s => {
          const count = purchaseLists.filter(pl => pl.status === s).length;
          const active = filterStatus === s.toString();
          return (
            <button key={s} onClick={() => setFilterStatus(active ? '' : s.toString())} style={{ ...pillStyle, backgroundColor: active ? statusColors[s].bg : colors.white, color: active ? statusColors[s].text : colors.textLight, border: `1px solid ${active ? statusColors[s].text + '30' : colors.border}` }}>
              {purchaseListStatusNames[s]} ({count})
            </button>
          );
        })}
      </div>

      {isLoadingPurchases && <div style={{ textAlign: 'center', padding: '32px', color: colors.textLight }}>טוען...</div>}

      {!isLoadingPurchases && filtered.map(pl => {
        const sc = statusColors[pl.status] || statusColors[0];
        return (
          <div key={pl.publicId} onClick={() => openDetail(pl)} style={{ backgroundColor: colors.white, borderRadius: '12px', border: `1px solid ${colors.border}`, padding: '14px 16px', marginBottom: '10px', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>{pl.name}</span>
                  <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: sc.bg, color: sc.text, fontWeight: '500' }}>
                    {pl.statusName || purchaseListStatusNames[pl.status]}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: colors.textLight }}>
                  <span>{fmtDate(pl.createdAt)}</span>
                  <span>{pl.purchasedItemCount}/{pl.itemCount} נרכשו</span>
                  {pl.budgetSourceName && <span>{pl.budgetSourceName}</span>}
                </div>
              </div>
              <div style={{ textAlign: 'left' }}>
                {pl.totalEstimated != null && pl.totalEstimated > 0 && (
                  <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>{fmt(pl.totalEstimated)}</span>
                )}
              </div>
            </div>
            {/* Mini progress bar */}
            {pl.itemCount > 0 && (
              <div style={{ marginTop: '8px', height: '3px', backgroundColor: colors.border, borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(pl.purchasedItemCount / pl.itemCount) * 100}%`, backgroundColor: colors.green, borderRadius: '2px' }} />
              </div>
            )}
          </div>
        );
      })}

      {!isLoadingPurchases && filtered.length === 0 && (
        <div style={{ backgroundColor: colors.white, borderRadius: '14px', padding: '48px 24px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <ShoppingCart size={28} color={colors.primary} />
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            {filterStatus ? 'אין רשימות בסטטוס זה' : 'אין רשימות קניות עדיין'}
          </h3>
          <p style={{ fontSize: '14px', color: colors.textLight }}>צרי רשימת קניות ראשונה</p>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div style={{ backgroundColor: colors.white, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '600px', maxHeight: '85vh', overflow: 'auto', padding: '24px' }} onMouseDown={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, margin: 0 }}>רשימת קניות חדשה</h2>
              <button onClick={() => setShowCreate(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <X size={22} color={colors.textLight} />
              </button>
            </div>
            {error && <div style={{ backgroundColor: colors.redLight, color: '#991b1b', padding: '10px 14px', borderRadius: '10px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <FormLabel text="שם הרשימה" required helpText={teacherPurchaseHelpTexts.listName.tooltip} />
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="למשל: קניות לחנוכה" style={inputStyle} />
                <HelperText text={teacherPurchaseHelpTexts.listName.helper} />
              </div>
              <div>
                <FormLabel text="מקור תקציב (אופציונלי)" helpText={teacherPurchaseHelpTexts.budgetSource.tooltip} />
                <select value={newSourceId} onChange={e => setNewSourceId(e.target.value)} style={inputStyle}>
                  <option value="">ללא שיוך</option>
                  {budgetSources.filter(bs => bs.isActive).map(bs => (
                    <option key={bs.publicId} value={bs.publicId}>{bs.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <FormLabel text="הערות" helpText={teacherPurchaseHelpTexts.notes.tooltip} />
                <textarea value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="הערות..." rows={2} style={{ ...inputStyle, resize: 'vertical' } as React.CSSProperties} />
              </div>
              <button onClick={handleCreate} disabled={creating} style={{ width: '100%', padding: '14px', backgroundColor: creating ? colors.border : colors.primary, color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: creating ? 'not-allowed' : 'pointer' }}>
                {creating ? 'יוצר...' : 'צור רשימה'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        itemName={deleteTarget?.name ?? ''}
        isLoading={deleting}
      />
    </div>
  );
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', fontSize: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', outline: 'none', backgroundColor: '#f9fafb', boxSizing: 'border-box', direction: 'rtl' };
const labelStyle: React.CSSProperties = { fontSize: '13px', fontWeight: '500', color: '#1f2937', display: 'block', marginBottom: '4px' };
const pillStyle: React.CSSProperties = { padding: '6px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 };
